import React from 'react';
import PropTypes from 'prop-types';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import PermissionGate from '../../common/PermissionGate';
import useAuth from '../../../hooks/useAuth';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DescriptionOutlined as DocxIcon,
  PictureAsPdfOutlined as PdfIcon,
  GroupAdd as AssignValidatorsIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const TaskMenu = ({
  anchorEl,
  selectedTile,
  onClose,
  onEdit,
  onDownloadPDF,
  onDownloadDOCX,
  onRemove,
  showNotification,
  isOwnerOrAdmin,
  onAssignValidators,
  onViewHistory
}) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      elevation={3}
      sx={{
        '& .MuiPaper-root': {
          minWidth: '180px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
        }
      }}
    >
      <PermissionGate permission="foundation.task.edit">
        <MenuItem onClick={() => {
          onEdit(selectedTile);
          onClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      </PermissionGate>

      <PermissionGate permission="foundation.task.download">
        <MenuItem onClick={() => {
          onDownloadPDF(selectedTile);
          onClose();
        }}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => {
          onDownloadDOCX(selectedTile);
          onClose();
        }}>
          <ListItemIcon>
            <DocxIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download docx</ListItemText>
        </MenuItem>
      </PermissionGate>

      <Divider />

      {/* Assign Validators — full access (both internal & external) */}
      {onAssignValidators && (
        <PermissionGate permission="project.validators.assign" exact>
          <MenuItem onClick={() => {
            onAssignValidators(selectedTile, 'both');
            onClose();
          }}>
            <ListItemIcon>
              <AssignValidatorsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Assign validators</ListItemText>
          </MenuItem>
        </PermissionGate>
      )}

      {/* Assign Validator — Internal only */}
      {onAssignValidators && (
        <PermissionGate permission="project.validators.assign.internal">
          <MenuItem onClick={() => {
            onAssignValidators(selectedTile, 'internal');
            onClose();
          }}>
            <ListItemIcon>
              <AssignValidatorsIcon fontSize="small" sx={{ color: '#1976d2' }} />
            </ListItemIcon>
            <ListItemText>Assign validators</ListItemText>
          </MenuItem>
        </PermissionGate>
      )}

      {/* Assign Validator — External only */}
      {onAssignValidators && (
        <PermissionGate permission="project.validators.assign.external">
          <MenuItem onClick={() => {
            onAssignValidators(selectedTile, 'external');
            onClose();
          }}>
            <ListItemIcon>
              <AssignValidatorsIcon fontSize="small" sx={{ color: '#2e7d32' }} />
            </ListItemIcon>
            <ListItemText>Assign validators</ListItemText>
          </MenuItem>
        </PermissionGate>
      )}

      {/* Validation History (Internal and External) — sees all */}
      {onViewHistory && (
        <PermissionGate permission="foundation.task.validation.history.all">
          <MenuItem onClick={() => {
            onViewHistory(selectedTile, 'both');
            onClose();
          }}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Validation History (Internal and External)</ListItemText>
          </MenuItem>
        </PermissionGate>
      )}

      {/* Validation History — filtered by user's own member type */}
      {onViewHistory && (
        <PermissionGate permission="foundation.task.validation.history" exact>
          <MenuItem onClick={() => {
            onViewHistory(selectedTile, 'member-type');
            onClose();
          }}>
            <ListItemIcon>
              <HistoryIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Validation History</ListItemText>
          </MenuItem>
        </PermissionGate>
      )}

      <PermissionGate permission="foundation.remove.topic">
        <MenuItem onClick={() => {
          if (selectedTile?.id === 'chat-prd' || selectedTile?.id === 'extract-prd') {
            showNotification({
              message: `Cannot remove core topic: ${selectedTile?.title}`,
              type: 'warning'
            });
          } else {
            onRemove(selectedTile);
          }
          onClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove topic</ListItemText>
        </MenuItem>
      </PermissionGate>
    </Menu>
  );
};

TaskMenu.propTypes = {
  anchorEl: PropTypes.object,
  selectedTile: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDownloadPDF: PropTypes.func.isRequired,
  onDownloadDOCX: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  isOwnerOrAdmin: PropTypes.bool,
  onAssignValidators: PropTypes.func,
  onViewHistory: PropTypes.func
};

export default React.memo(TaskMenu);
